<template>
<article class="dashboard">
    <!-- 查询条件 -->
    <section class="search-section">
      <el-form :model="searchForm" inline>
        <el-form-item>
          <el-radio-group v-model="searchForm.type" @change="handleTypeChange">
            <el-radio label="1">{{$t('日期')}}</el-radio>
            <el-radio label="2">{{$t('月份')}}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item>
          <el-date-picker v-model="searchForm.date" :type="searchForm.type === '1' ? 'date' : 'month'" :placeholder="searchForm.type === '1' ? $t('选择日期') : $t('选择月份')" :format="searchForm.type === '1' ? 'yyyy-MM-dd' : 'yyyy-MM'" :value-format="searchForm.type === '1' ? 'yyyy-MM-dd' : 'yyyy-MM'" @change="handleDateChange"></el-date-picker>
        </el-form-item>
      </el-form>
    </section>

    <!-- 基础数据卡片 -->
    <section class="basic-data">
      <div class="data-card">
        <div class="card-header">
          <span class="card-icon revenue">$</span>
          <span class="card-title">{{$t('集团营收')}}</span>
        </div>
        <div class="card-body">
          <span class="card-value">{{formatNumber(basicData.totalRevenue)}}</span>
        </div>
      </div>

      <div class="data-card">
        <div class="card-header">
          <span class="card-icon rate">%</span>
          <span class="card-title">{{$t('集团出租率')}}</span>
        </div>
        <div class="card-body">
          <span class="card-value">{{basicData.avgOccupancyRate}}%</span>
        </div>
      </div>

      <div class="data-card">
        <div class="card-header">
          <span class="card-icon adr">⌂</span>
          <span class="card-title">{{$t('集团ADR')}}</span>
        </div>
        <div class="card-body">
          <span class="card-value">{{basicData.avgADR}}</span>
        </div>
      </div>

      <div class="data-card">
        <div class="card-header">
          <span class="card-icon revpar">¤</span>
          <span class="card-title">{{$t('集团RevPAR')}}</span>
        </div>
        <div class="card-body">
          <span class="card-value">{{basicData.revPAR}}</span>
        </div>
      </div>
    </section>

    <!-- 数据趋势和TOP门店 -->
    <section class="content-section">
      <!-- 数据趋势 -->
      <div class="trend-section">
        <div class="section-header">
          <span class="section-title">{{$t('数据趋势')}}</span>
          <el-tabs v-model="activeTab" type="card" class="trend-tabs">
            <el-tab-pane :label="$t('集团营收')" name="revenue"></el-tab-pane>
            <el-tab-pane :label="$t('集团出租率')" name="occupancy"></el-tab-pane>
            <el-tab-pane :label="$t('集团ADR')" name="adr"></el-tab-pane>
            <el-tab-pane :label="$t('集团RevPAR')" name="revpar"></el-tab-pane>
          </el-tabs>
        </div>
        <div class="chart-container">
          <svg :viewBox="`0 0 ${chartWidth} ${chartHeight}`" class="trend-chart">
            <!-- 网格线 -->
            <g class="grid-lines">
              <line v-for="i in 5" :key="'h'+i" :x1="padding" :y1="padding + (chartInnerHeight / 4) * (i - 1)" :x2="chartWidth - padding" :y2="padding + (chartInnerHeight / 4) * (i - 1)" stroke="#DEE3EE" stroke-dasharray="4,4"></line>
              <line v-for="(label, i) in xLabels" :key="'v'+i" :x1="padding + (chartInnerWidth / (xLabels.length - 1 || 1)) * i" :y1="padding" :x2="padding + (chartInnerWidth / (xLabels.length - 1 || 1)) * i" :y2="chartHeight - padding" stroke="#DEE3EE" stroke-dasharray="4,4"></line>
            </g>
            <!-- Y轴标签 -->
            <g class="y-labels">
              <text v-for="(label, i) in yLabels" :key="'yl'+i" :x="padding - 10" :y="padding + (chartInnerHeight / 4) * i + 4" text-anchor="end" class="axis-label">{{label}}</text>
            </g>
            <!-- X轴标签（第一个和最后一个总是显示，中间每隔2个点显示一次） -->
            <g class="x-labels">
              <text v-for="(label, i) in xLabels" :key="'xl'+i" :x="padding + (chartInnerWidth / (xLabels.length - 1 || 1)) * i" :y="chartHeight - 10" text-anchor="middle" class="axis-label" :style="{ display: shouldShowXLabel(i) ? 'block' : 'none' }">{{label}}</text>
            </g>
            <!-- 当前选中的趋势曲线 -->
            <path :d="currentChartPath" fill="none" :stroke="currentChartColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="chart-line"></path>
            <!-- 数据点 -->
            <g v-for="(point, index) in chartPoints" :key="'point'+index">
              <circle :cx="point.x" :cy="point.y" r="3" :fill="currentChartColor" class="data-point" @mouseenter="showTooltip($event, point, index)" @mouseleave="hideTooltip"></circle>
              <circle :cx="point.x" :cy="point.y" r="8" fill="transparent" class="data-point-hover" @mouseenter="showTooltip($event, point, index)" @mouseleave="hideTooltip"></circle>
            </g>
            <!-- Tooltip -->
            <g v-if="tooltipVisible" class="tooltip-group">
              <rect :x="tooltipX - 40" :y="tooltipY - 45" width="80" height="35" rx="4" fill="rgba(0,0,0,0.8)"></rect>
              <text :x="tooltipX" :y="tooltipY - 25" text-anchor="middle" fill="#fff" font-size="11">{{tooltipDate}}</text>
              <text :x="tooltipX" :y="tooltipY - 12" text-anchor="middle" fill="#fff" font-size="13" font-weight="600">{{tooltipValue}}</text>
              <!-- 小三角 -->
              <polygon :points="`${tooltipX - 6},${tooltipY - 10} ${tooltipX + 6},${tooltipY - 10} ${tooltipX},${tooltipY}`" fill="rgba(0,0,0,0.8)"></polygon>
            </g>
          </svg>
        </div>
      </div>

      <!-- TOP门店 -->
      <div class="top-section">
        <div class="section-header">
          <span class="section-title">{{currentTopTitle}}</span>
          <el-button type="text" size="mini" icon="el-icon-download" @click="handleExportTop" class="export-btn"></el-button>
        </div>
        <div class="top-list">
          <div v-for="(item, index) in currentTopStores" :key="index" class="top-item">
            <span class="top-rank" :class="getRankClass(index)">{{index + 1}}</span>
            <span class="top-name">{{item.name}}</span>
            <span class="top-value">{{formatTopValue(item)}}</span>
            <span class="top-percent" v-if="item.percent !== undefined">{{item.percent}}%</span>
          </div>
        </div>
      </div>
    </section>
  </article>
</template>

<script>
import { intl } from "@tci18n/vue2";import reportAPI from '@/view/organization/api/report';

export default {
  name: 'Dashboard',
  data() {
    return {
      searchForm: {
        date: '',
        type: '1' // 1-日期模式，2-月份模式
      },
      activeTab: 'revenue',
      chartWidth: 700,
      chartHeight: 300,
      padding: 50,
      // tooltip相关
      tooltipVisible: false,
      tooltipX: 0,
      tooltipY: 0,
      tooltipValue: '',
      tooltipDate: '',
      // 基础数据（从接口获取）
      basicData: {
        totalRevenue: 0,
        avgOccupancyRate: '0.00',
        avgADR: '0.00',
        revPAR: '0.00'
      },
      // TOP门店数据
      topStores: {
        revenue: [],
        occupancy: [],
        adr: [],
        revpar: []
      },
      // 原始日期数组（用于tooltip显示完整日期）
      originalDates: [],
      // 图表数据
      chartData: {
        revenue: [],
        occupancy: [],
        adr: [],
        revpar: []
      },
      xLabels: []
    };
  },
  computed: {
    chartInnerWidth() {
      return this.chartWidth - this.padding * 2;
    },
    chartInnerHeight() {
      return this.chartHeight - this.padding * 2;
    },
    yLabels() {
      const data = this.chartData[this.activeTab];
      if (!data || data.length === 0) return [];
      const max = Math.max(...data);
      const min = Math.min(...data);
      const range = max - min;
      const step = range / 4;
      return [max.toFixed(0), (max - step).toFixed(0), (max - step * 2).toFixed(0), (max - step * 3).toFixed(0), min.toFixed(0)];
    },
    currentChartPath() {
      return this.generatePath(this.chartData[this.activeTab]);
    },
    chartPoints() {
      const data = this.chartData[this.activeTab];
      if (!data || data.length === 0) return [];
      const allValues = [...data];
      const max = Math.max(...allValues);
      const min = Math.min(...allValues);
      const range = max - min || 1;

      return data.map((value, index) => {
        const x = this.padding + index / (data.length - 1) * this.chartInnerWidth;
        const y = this.padding + (max - value) / range * this.chartInnerHeight;
        return { x, y, value, index };
      });
    },
    currentChartColor() {
      // 与卡片图标颜色保持一致（使用渐变色的起始颜色）
      const colors = {
        revenue: '#667EEA', // 对应卡片revenue渐变色起始色
        occupancy: '#11998E', // 对应卡片rate渐变色起始色
        adr: '#4FACFE', // 对应卡片adr渐变色起始色
        revpar: '#F093FB' // 对应卡片revpar渐变色起始色
      };
      return colors[this.activeTab] || '#667EEA';
    },
    currentTopTitle() {
      const titles = {
        revenue: intl.$t("营收TOP门店"),
        occupancy: intl.$t("出租率TOP门店"),
        adr: intl.$t("ADR TOP门店"),
        revpar: intl.$t("RevPar TOP门店")
      };
      return titles[this.activeTab] || intl.$t("营收TOP门店");
    },
    currentTopStores() {
      return this.topStores[this.activeTab] || [];
    }
  },
  created() {
    // 设置默认日期为当天的前一天（日期模式）或当月（月份模式）
    const now = new Date();
    if (this.searchForm.type === '1') {
      // 日期模式：默认前一天
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      this.searchForm.date = yesterday.toISOString().split('T')[0];
    } else {
      // 月份模式：默认当月
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      this.searchForm.date = `${year}-${month}`;
    }
    this.fetchDashboardData();
  },

  methods: {
    handleDateChange(val) {
      if (val) {
        this.fetchDashboardData();
      }
    },
    handleTypeChange() {
      // 切换类型时重新设置日期格式
      const now = new Date();
      if (this.searchForm.type === '1') {
        // 切换到日期模式：默认前一天
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        this.searchForm.date = yesterday.toISOString().split('T')[0];
      } else {
        // 切换到月份模式：默认当月
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        this.searchForm.date = `${year}-${month}`;
      }
      // 切换类型后刷新数据
      this.fetchDashboardData();
    },
    formatNumber(num) {
      return Number(num).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    shouldShowXLabel(index) {
      const total = this.xLabels.length;
      // 第一个和最后一个总是显示
      if (index === 0 || index === total - 1) {
        return true;
      }
      // 月份模式下显示更密集（每隔2个点）
      if (this.searchForm.type === '2') {
        return index % 2 === 0;
      }
      // 日期模式下每隔5个点显示一次
      return index % 5 === 0;
    },
    formatTopValue(item) {
      if (this.activeTab === 'revenue') {
        return this.formatNumber(item.revenue);
      } else if (this.activeTab === 'occupancy') {
        return (item.rentRate * 100).toFixed(2) + '%';
      } else if (this.activeTab === 'adr') {
        return item.adr.toFixed(2);
      } else if (this.activeTab === 'revpar') {
        return item.revPar.toFixed(2);
      }
      return '';
    },
    formatTopValue(item) {
      if (this.activeTab === 'revenue') {
        return this.formatNumber(item.revenue);
      } else if (this.activeTab === 'occupancy') {
        return (item.rentRate * 100).toFixed(2) + '%';
      } else if (this.activeTab === 'adr') {
        return item.adr.toFixed(2);
      } else if (this.activeTab === 'revpar') {
        return item.revPar.toFixed(2);
      }
      return '';
    },
    getRankClass(index) {
      if (index === 0) return 'first';
      if (index === 1) return 'second';
      if (index === 2) return 'third';
      return '';
    },
    async fetchDashboardData() {
      try {
        // 月份模式下，将日期转换为 yyyy-MM-01 格式
        let date = this.searchForm.date;
        if (this.searchForm.type === '2') {
          date = this.searchForm.date + '-01';
        }
        console.log('开始调用接口，日期:', date, '类型:', this.searchForm.type);
        const params = {
          date: date,
          type: parseInt(this.searchForm.type)
        };
        const res = await reportAPI.blocDashboard(params);
        console.log('接口返回:', res);

        // 兼容不同的code格式（字符串'0'、数字0、数字200）
        if (res && res.data) {
          // 检查code是否成功
          const successCodes = ['0', 0, 200, '200'];
          if (successCodes.includes(res.code)) {
            console.log('接口调用成功，开始解析数据');
            this.parseDashboardData(res.data);
          } else {
            console.log('接口返回失败，code:', res.code, 'message:', res.message);
          }
        } else {
          console.log('接口返回数据为空:', res);
        }
      } catch (err) {
        console.error('获取仪表盘数据失败:', err);
      }
    },
    parseDashboardData(data) {
      console.log('开始解析数据:', data);
      // 月份模式下，将日期转换为 yyyy-MM-01 格式以匹配接口返回
      let targetDate = this.searchForm.date;
      if (this.searchForm.type === '2' && targetDate.length === 7) {
        targetDate = targetDate + '-01';
      }
      console.log('目标日期:', targetDate);

      // 从incomeList中筛选目标日期的收入数据
      let totalRevenue = 0;
      if (data.incomeList && Array.isArray(data.incomeList)) {
        console.log('incomeList长度:', data.incomeList.length);
        const incomeItem = data.incomeList.find((item) => item.date === targetDate);
        console.log('找到的收入项:', incomeItem);
        if (incomeItem) {
          totalRevenue = parseFloat(incomeItem.income) || 0;
        }
      }
      console.log('计算的总收入:', totalRevenue);

      // 从rentRateList中筛选目标日期的出租率数据（接口返回的是小数，需要乘以100）
      let avgOccupancyRate = 0;
      if (data.rentRateList && Array.isArray(data.rentRateList)) {
        const rentRateItem = data.rentRateList.find((item) => item.date === targetDate);
        if (rentRateItem) {
          avgOccupancyRate = (parseFloat(rentRateItem.rentRate) || 0) * 100;
        }
      }

      // 从adrList中筛选目标日期的ADR数据
      let avgADR = 0;
      if (data.adrList && Array.isArray(data.adrList)) {
        const adrItem = data.adrList.find((item) => item.date === targetDate);
        if (adrItem) {
          avgADR = parseFloat(adrItem.adr) || 0;
        }
      }

      // 从revParList中筛选目标日期的RevPAR数据
      let revPAR = 0;
      if (data.revParList && Array.isArray(data.revParList)) {
        const revParItem = data.revParList.find((item) => item.date === targetDate);
        if (revParItem) {
          revPAR = parseFloat(revParItem.revPar) || 0;
        }
      }

      // 设置基础数据
      this.basicData = {
        totalRevenue,
        avgOccupancyRate: avgOccupancyRate.toFixed(2),
        avgADR: avgADR.toFixed(2),
        revPAR: revPAR.toFixed(2)
      };
      console.log('更新后的basicData:', this.basicData);

      // 解析营收TOP门店数据（rate是占比，需要乘以100并保留两位小数）
      if (data.hotelIncomeList && Array.isArray(data.hotelIncomeList)) {
        this.topStores.revenue = data.hotelIncomeList.map((item) => ({
          name: item.hotelName || intl.$t(`酒店{slot0}`, { "slot0": item.hotelVid }),
          revenue: parseFloat(item.income) || 0,
          percent: parseFloat(((parseFloat(item.rate) || 0) * 100).toFixed(2))
        })).sort((a, b) => b.revenue - a.revenue).slice(0, 16);
      } else {
        this.topStores.revenue = [];
      }

      // 解析出租率TOP门店数据
      if (data.hotelRentRateList && Array.isArray(data.hotelRentRateList)) {
        this.topStores.occupancy = data.hotelRentRateList.map((item) => ({
          name: item.hotelName || intl.$t(`酒店{slot0}`, { "slot0": item.hotelVid }),
          rentRate: parseFloat(item.rentRate) || 0
        })).sort((a, b) => b.rentRate - a.rentRate).slice(0, 16);
      } else {
        this.topStores.occupancy = [];
      }

      // 解析ADR TOP门店数据
      if (data.hotelAdrList && Array.isArray(data.hotelAdrList)) {
        this.topStores.adr = data.hotelAdrList.map((item) => ({
          name: item.hotelName || intl.$t(`酒店{slot0}`, { "slot0": item.hotelVid }),
          adr: parseFloat(item.adr) || 0
        })).sort((a, b) => b.adr - a.adr).slice(0, 16);
      } else {
        this.topStores.adr = [];
      }

      // 解析RevPar TOP门店数据
      if (data.hotelRevParList && Array.isArray(data.hotelRevParList)) {
        this.topStores.revpar = data.hotelRevParList.map((item) => ({
          name: item.hotelName || intl.$t(`酒店{slot0}`, { "slot0": item.hotelVid }),
          revPar: parseFloat(item.revPar) || 0
        })).sort((a, b) => b.revPar - a.revPar).slice(0, 16);
      } else {
        this.topStores.revpar = [];
      }

      // 解析图表数据（趋势数据）
      if (data.incomeList && Array.isArray(data.incomeList)) {
        // 保存原始日期格式用于tooltip显示
        this.originalDates = data.incomeList.map((item) => item.date || '');
        // 月份模式下X轴显示简化的月份格式（yyyy-MM），日期模式显示完整日期
        this.xLabels = data.incomeList.map((item) => {
          const date = item.date || '';
          if (this.searchForm.type === '2' && date.length >= 7) {
            return date.substring(0, 7); // 截取 yyyy-MM 部分
          }
          return date;
        });
        this.chartData.revenue = data.incomeList.map((item) => parseFloat(item.income) || 0);
      }

      // 出租率趋势数据（接口返回的是小数，需要乘以100并保留两位小数）
      if (data.rentRateList && Array.isArray(data.rentRateList)) {
        this.chartData.occupancy = data.rentRateList.map((item) => parseFloat(((parseFloat(item.rentRate) || 0) * 100).toFixed(2)));
      }

      // ADR趋势数据（保留两位小数）
      if (data.adrList && Array.isArray(data.adrList)) {
        this.chartData.adr = data.adrList.map((item) => parseFloat((parseFloat(item.adr) || 0).toFixed(2)));
      }

      // RevPAR趋势数据（保留两位小数）
      if (data.revParList && Array.isArray(data.revParList)) {
        this.chartData.revpar = data.revParList.map((item) => parseFloat((parseFloat(item.revPar) || 0).toFixed(2)));
      }

      // 收入趋势数据（保留两位小数）
      if (data.incomeList && Array.isArray(data.incomeList)) {
        this.chartData.revenue = data.incomeList.map((item) => parseFloat((parseFloat(item.income) || 0).toFixed(2)));
      }
    },
    generatePath(data) {
      if (!data || data.length === 0) return '';
      const allValues = [...data];
      const max = Math.max(...allValues);
      const min = Math.min(...allValues);
      const range = max - min || 1;

      const points = data.map((value, index) => {
        const x = this.padding + index / (data.length - 1) * this.chartInnerWidth;
        const y = this.padding + (max - value) / range * this.chartInnerHeight;
        return { x, y };
      });

      if (points.length < 2) return '';

      let path = `M ${points[0].x} ${points[0].y}`;

      for (let i = 0; i < points.length - 1; i++) {
        const p1 = points[i];
        const p2 = points[i + 1];

        // 如果两个点Y值相同或非常接近，使用直线连接，避免失真
        if (Math.abs(p1.y - p2.y) < 2) {
          path += ` L ${p2.x} ${p2.y}`;
        } else {
          // 使用平滑曲线连接，但保持控制点靠近数据点
          const midX = (p1.x + p2.x) / 2;
          // 计算控制点，使曲线更柔和但不产生大的波动
          const cp1x = p1.x + (p2.x - p1.x) * 0.25;
          const cp1y = p1.y;
          const cp2x = p1.x + (p2.x - p1.x) * 0.75;
          const cp2y = p2.y;

          path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
        }
      }

      return path;
    },
    showTooltip(event, point, index) {
      this.tooltipVisible = true;
      this.tooltipX = point.x;
      this.tooltipY = point.y;

      // 获取原始日期格式（2026-04-25）
      if (this.activeTab === 'revenue' && this.chartData.revenue.length > index) {
        const dateIndex = this.chartData.revenue.length - this.chartData.occupancy.length + index;
        if (this.originalDates && this.originalDates[dateIndex]) {
          this.tooltipDate = this.originalDates[dateIndex];
        } else {
          this.tooltipDate = '';
        }
      } else if (this.originalDates && this.originalDates[index]) {
        this.tooltipDate = this.originalDates[index];
      } else {
        this.tooltipDate = '';
      }

      // 根据当前标签页格式化数值（去除货币标识）
      if (this.activeTab === 'revenue') {
        this.tooltipValue = Number(point.value).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      } else if (this.activeTab === 'occupancy') {
        this.tooltipValue = point.value.toFixed(2) + '%';
      } else {
        this.tooltipValue = point.value.toFixed(2);
      }
    },
    hideTooltip() {
      this.tooltipVisible = false;
    },
    handleExportTop() {
      const stores = this.currentTopStores || [];
      if (!stores.length) {
        this.$message && this.$message.warning(intl.$t("暂无数据"));
        return;
      }

      // 构建列数据：排名 / 名称 / 数值（/ 占比 - 营收模式下附加）
      const isRevenue = this.activeTab === 'revenue';
      const rows = stores.map((item, index) => {
        let value = '';
        if (this.activeTab === 'revenue') {
          value = this.formatNumber(item.revenue);
        } else if (this.activeTab === 'occupancy') {
          value = (item.rentRate * 100).toFixed(2) + '%';
        } else if (this.activeTab === 'adr') {
          value = Number(item.adr).toFixed(2);
        } else if (this.activeTab === 'revpar') {
          value = Number(item.revPar).toFixed(2);
        }
        const row = [index + 1, item.name || '', value];
        if (isRevenue && item.percent !== undefined && item.percent !== null) {
          row.push(item.percent + '%');
        } else if (isRevenue) {
          row.push('');
        }
        return row;
      });

      // 使用CSV方式导出为.xls（不需要额外依赖），无列名，仅3列数据
      const csvContent = rows.map((row) =>
      row.map((cell) => {
        const str = String(cell == null ? '' : cell);
        // 若包含逗号/引号/换行则用双引号包裹并转义
        if (/[,"]/.test(str) || /\r?\n/.test(str)) {
          return '"' + str.replace(/"/g, '""') + '"';
        }
        return str;
      }).join(',')
      ).join('\n');

      // 添加BOM，避免中文乱码
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], {
        type: 'application/vnd.ms-excel;charset=utf-8;'
      });

      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const fileName = `${this.currentTopTitle}-${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}.xls`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      if (this.$message) this.$message.success(intl.$t("导出Excel成功"));
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/style/const";
@import "@/style/mixins";

.dashboard {
  padding: 16px;
  background: $bg-color;
  height: calc(100vh - 120px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.search-section {
  background: #fff;
  padding: 16px;
  margin-bottom: 0;
  border-bottom: 1px solid #f0f0f0;

  :deep(.el-form-item) {
    margin-bottom: 0;
    margin-right: 16px;

    .el-form-item__label {
      font-size: 14px;
      color: $text-color;
      margin-right: 8px;
    }
  }

  :deep(.el-date-editor) {
    width: 180px;
  }
}

.header-switch {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;

  :deep(.el-radio-group) {
    background: #fff;
    padding: 4px;
    border-radius: 4px;
  }
}

.basic-data {
  display: flex;
  background: #fff;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px;
  flex-shrink: 0;

  .data-card {
    flex: 1;
    background: transparent;
    padding: 12px;
    border-right: 1px solid #f0f0f0;

    &:last-child {
      border-right: none;
    }

    .card-header {
      display: flex;
      align-items: center;
      margin-bottom: 8px;

      .card-icon {
        width: 30px;
        height: 30px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        font-weight: bold;
        margin-right: 10px;

        &.revenue {
          background: linear-gradient(135deg, #667EEA 0%, #764BA2 100%);
          color: #fff;
        }

        &.rate {
          background: linear-gradient(135deg, #11998E 0%, #38EF7D 100%);
          color: #fff;
        }

        &.adr {
          background: linear-gradient(135deg, #4FACFE 0%, #00F2FE 100%);
          color: #fff;
        }

        &.revpar {
          background: linear-gradient(135deg, #F093FB 0%, #F5576C 100%);
          color: #fff;
        }
      }

      .card-title {
        font-size: 13px;
        color: $text-disabled-color;
      }
    }

    .card-body {
      display: flex;
      align-items: baseline;
      gap: 8px;

      .card-value {
        font-size: 24px;
        font-weight: 600;
        color: $title-color;
      }

      .card-trend {
        display: flex;
        align-items: center;
        font-size: 12px;
        padding: 4px 8px;
        border-radius: 12px;

        &.up {
          color: $success-color;
          background: rgba(106, 206, 150, 0.1);
        }

        &.down {
          color: $error-color;
          background: rgba(255, 77, 79, 0.1);
        }

        .trend-icon {
          margin-right: 4px;
        }
      }
    }
  }
}

.content-section {
  display: flex;
  gap: 16px;
  flex: 1;
  overflow: hidden;

  .trend-section {
    flex: 2;
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    min-width: 0;


    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      flex-shrink: 0;

      .section-title {
        font-size: 15px;
        font-weight: 600;
        color: $title-color;
      }
    }

    .chart-container {
      flex: 1;
      width: 100%;
      overflow-x: auto;
      display: flex;
      align-items: center;
    }

    .trend-chart {
      width: 100%;
      min-width: 600px;
      height: auto;
    }

    .axis-label {
      font-size: 11px;
      fill: $text-disabled-color;
    }

    .legend-text {
      font-size: 11px;
      fill: $text-color;
    }

    .chart-line {
      transition: stroke-dasharray 0.3s ease;
    }
  }

  .top-section {
    flex: 1;
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    min-width: 0;


    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      flex-shrink: 0;

      .section-title {
        font-size: 15px;
        font-weight: 600;
        color: $title-color;
      }

      .export-btn {
        padding: 4px;
        min-width: 24px;
        color: $text-disabled-color;

        &:hover {
          color: $theme-color;
          background: rgba(73, 124, 246, 0.05);
        }

        :deep(.el-icon-download) {
          font-size: 14px;
        }
      }
    }

    .top-list {
      flex: 1;
      overflow-y: auto;
    }

    .top-item {
      display: flex;
      align-items: center;
      padding: 8px 0;
      border-bottom: 1px solid #F5F6FA;

      &:last-child {
        border-bottom: none;
      }

      .top-rank {
        width: 20px;
        height: 20px;
        border-radius: 50%;
        background: #E8ECF0;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 600;
        color: $text-disabled-color;
        margin-right: 10px;

        &.first {
          background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
          color: #fff;
        }

        &.second {
          background: linear-gradient(135deg, #C0C0C0 0%, #A0A0A0 100%);
          color: #fff;
        }

        &.third {
          background: linear-gradient(135deg, #CD7F32 0%, #B87333 100%);
          color: #fff;
        }
      }

      .top-name {
        flex: 1;
        font-size: 12px;
        color: $text-color;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-right: 8px;
      }

      .top-value {
        font-size: 13px;
        font-weight: 600;
        color: $title-color;
        margin-right: 8px;
        min-width: 65px;
        text-align: right;
      }

      .top-percent {
        font-size: 13px;
        font-weight: 600;
        color: $theme-color;
        min-width: 50px;
        text-align: right;
      }

      .top-rate {
        font-size: 12px;
        font-weight: 500;
        padding: 2px 6px;
        border-radius: 10px;

        &.up {
          color: $success-color;
          background: rgba(106, 206, 150, 0.1);
        }

        &.down {
          color: $error-color;
          background: rgba(255, 77, 79, 0.1);
        }
      }
    }
  }
}

:deep(.el-tabs__header) {
  margin: 0 0 16px 0;
}

:deep(.el-tabs__nav) {
  border-bottom: none;
}

:deep(.el-tabs__item) {
  padding: 6px 16px;
  font-size: 13px;
  border-radius: 4px 4px 0 0;
  margin-right: 8px;

  &:hover {
    background: rgba(73, 124, 246, 0.05);
  }
}

:deep(.el-tabs__item.is-active) {
  background: $theme-color;
  color: #fff;
  margin-right: 0;
}

:deep(.el-tabs__content) {
  padding: 0;
}
</style>
